import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { ArrowLeft, Users, CalendarDays, CheckCircle, AlertCircle, Trophy } from 'lucide-react';
import { generateId } from '../../utils/helpers';

export default function TeamMigrationTool({ clubs = [], onBack }) {
  const [teams, setTeams] = useState([]);

  const [sourceClubId, setSourceClubId] = useState('');
  const [sourceTeamId, setSourceTeamId] = useState('');

  const [destClubId, setDestClubId] = useState('');
  const [destTeamId, setDestTeamId] = useState('');

  const [sourceTournaments, setSourceTournaments] = useState([]);
  const [destTournaments, setDestTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');

  const [clubGames, setClubGames] = useState([]);
  const [selectedGameIds, setSelectedGameIds] = useState(new Set());

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch Source Teams & Tournaments when Source Club changes
  useEffect(() => {
    if (!sourceClubId) return;
    const fetchSourceData = async () => {
      try {
        const qTeams = query(collection(db, 'teams'), where('clubId', '==', sourceClubId));
        const qTourneys = query(collection(db, 'tournaments'), where('clubId', '==', sourceClubId));
        
        const [teamsSnap, tourneysSnap, gamesSnap] = await Promise.all([
          getDocs(qTeams), 
          getDocs(qTourneys),
          getDocs(query(collection(db, 'games'), where('clubId', '==', sourceClubId)))
        ]);
        
        const teamsData = teamsSnap.docs.map(d => ({id: d.id, ...d.data()}));
        const tourneysData = tourneysSnap.docs.map(d => ({id: d.id, ...d.data()}));
        const gamesData = gamesSnap.docs.map(d => ({id: d.id, ...d.data()}));
        
        setTeams(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          return [...prev, ...teamsData.filter(d => !existingIds.has(d.id))];
        });
        setSourceTournaments(tourneysData);
        setClubGames(gamesData);
        setSelectedGameIds(new Set()); // Reset selection
      } catch (err) {
        console.error("Error fetching source data", err);
      }
    };
    fetchSourceData();
  }, [sourceClubId]);

  // Fetch Dest Teams & Tournaments when Dest Club changes
  useEffect(() => {
    if (!destClubId) return;
    const fetchDestData = async () => {
      try {
        const qTeams = query(collection(db, 'teams'), where('clubId', '==', destClubId));
        const qTourneys = query(collection(db, 'tournaments'), where('clubId', '==', destClubId));
        
        const [teamsSnap, tourneysSnap] = await Promise.all([getDocs(qTeams), getDocs(qTourneys)]);
        
        const teamsData = teamsSnap.docs.map(d => ({id: d.id, ...d.data()}));
        const tourneysData = tourneysSnap.docs.map(d => ({id: d.id, ...d.data()}));
        
        setTeams(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          return [...prev, ...teamsData.filter(d => !existingIds.has(d.id))];
        });
        setDestTournaments(tourneysData);
      } catch (err) {
        console.error("Error fetching dest data", err);
      }
    };
    fetchDestData();
  }, [destClubId]);

  const handleMigrate = async () => {
    if(!sourceTeamId || !destTeamId) {
       setMessage({ type: 'error', text: "Please select both a source and destination team." });
       return;
    }
    if(sourceTeamId === destTeamId) {
       setMessage({ type: 'error', text: "Source and Destination teams cannot be the same."});
       return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // 1. Get Source Team
      const sourceDoc = await getDoc(doc(db, 'teams', sourceTeamId));
      if(!sourceDoc.exists()) throw new Error("Source team not found in database.");
      const sourcePlayers = sourceDoc.data().players || [];

      if(sourcePlayers.length === 0) {
        setMessage({ type: 'error', text: "Source team currently has 0 players assigned. Nothing to migrate."});
        setLoading(false);
        return;
      }

      // 2. Add players to dest team using arrayUnion safely
      const destRef = doc(db, 'teams', destTeamId);
      
      // Safety check: Does destination team exist?
      const destDoc = await getDoc(destRef);
      if(!destDoc.exists()) throw new Error("Destination team not found in database.");

      await updateDoc(destRef, {
        players: arrayUnion(...sourcePlayers)
      });

      setMessage({ 
        type: 'success', 
        text: `Phase 1 Complele! Successfully copied ${sourcePlayers.length} players to the destination team.` 
      });
    } catch(err) {
      console.error(err);
      setMessage({ type: 'error', text: "Error during migration: " + err.message });
    }
    setLoading(false);
  };

  const handleMigrateGames = async () => {
    if(!sourceClubId || !destClubId || !destTeamId || selectedGameIds.size === 0) {
      setMessage({ type: 'error', text: "Select Source Club, Dest Club, Dest Team, and at least 1 game." });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const gamesToCopy = clubGames.filter(g => selectedGameIds.has(g.id));

      for (const game of gamesToCopy) {
        const sourceGameTournament = sourceTournaments.find(t => t.id === game.tournamentId);
        const matchedDestTournament = destTournaments.find(t => t.name === sourceGameTournament?.name);

        const newGameId = generateId('game');
        const newGameData = {
          ...game,
          id: newGameId,
          teamId: destTeamId,
          clubId: destClubId,
          tournamentId: matchedDestTournament ? matchedDestTournament.id : null, 
          copiedFromLegacyId: game.id // Keep traceability
        };
        await setDoc(doc(db, 'games', newGameId), newGameData);
      }

      setMessage({ 
        type: 'success', 
        text: `Phase 2 Complete! Successfully cloned ${gamesToCopy.length} games to ${teams.find(t=>t.id===destTeamId)?.name}.` 
      });
      setSelectedGameIds(new Set());
    } catch(err) {
      console.error(err);
      setMessage({ type: 'error', text: "Error during game duplication: " + err.message });
    }
    setLoading(false);
  };

  const handleMigrateTournament = async () => {
    if(!sourceClubId || !destClubId || !selectedTournamentId) {
      setMessage({ type: 'error', text: "Select Source Club, Dest Club, and a Tournament to copy." });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const tourneyToCopy = sourceTournaments.find(t => t.id === selectedTournamentId);
      if(!tourneyToCopy) throw new Error("Invalid tournament source.");

      const newTourneyId = generateId('tournament');
      const newTourneyData = {
        ...tourneyToCopy,
        id: newTourneyId,
        clubId: destClubId,
        teams: [],
        rosters: {},
        copiedFromLegacyId: tourneyToCopy.id
      };

      await setDoc(doc(db, 'tournaments', newTourneyId), newTourneyData);

      // Optimistically update Dest Tournaments so phase 2 game copies see it
      setDestTournaments(prev => [...prev, newTourneyData]);

      setMessage({ 
        type: 'success', 
        text: `Phase 1.5 Complete! Successfully cloned tournament "${tourneyToCopy.name}" to destination club.` 
      });
    } catch(err) {
      console.error(err);
      setMessage({ type: 'error', text: "Error copying tournament: " + err.message });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
       <button onClick={onBack} className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
         <ArrowLeft size={20} /> Back to Dashboard
       </button>
       
       <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
         <div className="p-6 bg-indigo-600 border-b border-indigo-700 text-white">
           <h2 className="text-2xl font-bold flex items-center gap-2"><Trophy size={24} /> Phase 1.5: Tournament Migration</h2>
           <p className="opacity-90 mt-1">Copy entire tournaments from legacy clubs to duplicate clubs.</p>
         </div>

         <div className="p-6">
           {message && (
             <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${message.type === 'error' ? 'bg-red-50 text-red-800 border-red-200' : 'bg-green-50 text-green-800 border-green-200'} border`}>
               {message.type === 'error' ? <AlertCircle className="mt-0.5 flex-shrink-0" size={20}/> : <CheckCircle className="mt-0.5 flex-shrink-0" size={20}/>}
               <p className="font-medium">{message.text}</p>
             </div>
           )}

           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <label className="block mb-2 font-bold text-gray-700">Source Club</label>
                <select value={sourceClubId} onChange={(e) => { setSourceClubId(e.target.value); setSourceTeamId(''); setSelectedTournamentId(''); }} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white mb-4">
                  <option value="">-- Select Origin Club --</option>
                  {clubs.sort((a,b) => a.name.localeCompare(b.name)).map(c => (
                     <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>

                <label className="block mb-2 font-bold text-gray-700">Tournament to Duplicate</label>
                <select value={selectedTournamentId} onChange={(e) => setSelectedTournamentId(e.target.value)} disabled={!sourceClubId} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white disabled:opacity-50">
                  <option value="">-- Select Tournament --</option>
                  {sourceTournaments.map(t => (
                     <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <label className="block mb-2 font-bold text-blue-900">Destination Club</label>
                <select value={destClubId} onChange={(e) => { setDestClubId(e.target.value); setDestTeamId(''); }} className="w-full p-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white mb-4">
                  <option value="">-- Select Destination Club --</option>
                  {clubs.sort((a,b) => a.name.localeCompare(b.name)).map(c => (
                     <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="text-xs text-blue-700 mt-2">Tournament will be perfectly cloned here, with empty rosters.</p>
              </div>
           </div>

           <div className="flex justify-end gap-3">
             <button 
               onClick={handleMigrateTournament} 
               disabled={loading || !selectedTournamentId || !destClubId}
               className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg shadow font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
             >
               <Trophy size={20} />
               {loading ? 'Duplicating...' : 'Phase 1.5: Duplicate Tournament'}
             </button>
           </div>
         </div>
       </div>

       <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
         <div className="p-6 bg-indigo-600 border-b border-indigo-700 text-white">
           <h2 className="text-2xl font-bold flex items-center gap-2"><Users size={24} /> Phase 1: Player Migration</h2>
           <p className="opacity-90 mt-1">Safely copy players to duplicate teams.</p>
         </div>

         <div className="p-6">
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <label className="block mb-2 font-bold text-gray-700">Source Club</label>
                <select value={sourceClubId} onChange={(e) => { setSourceClubId(e.target.value); setSourceTeamId(''); setSelectedTournamentId(''); }} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white mb-4">
                  <option value="">-- Select Origin Club --</option>
                  {clubs.sort((a,b) => a.name.localeCompare(b.name)).map(c => (
                     <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>

                <label className="block mb-2 font-bold text-gray-700">Source Team</label>
                <select value={sourceTeamId} onChange={(e) => setSourceTeamId(e.target.value)} disabled={!sourceClubId} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white disabled:opacity-50">
                  <option value="">-- Select Origin Team --</option>
                  {teams.filter(t => t.clubId === sourceClubId).sort((a,b) => a.name.localeCompare(b.name)).map(t => (
                     <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">Players will be read from this team.</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <label className="block mb-2 font-bold text-blue-900">Destination Club</label>
                <select value={destClubId} onChange={(e) => { setDestClubId(e.target.value); setDestTeamId(''); }} className="w-full p-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white mb-4">
                  <option value="">-- Select Destination Club --</option>
                  {clubs.sort((a,b) => a.name.localeCompare(b.name)).map(c => (
                     <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>

                <label className="block mb-2 font-bold text-blue-900">Destination Team</label>
                <select value={destTeamId} onChange={(e) => setDestTeamId(e.target.value)} disabled={!destClubId} className="w-full p-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white disabled:opacity-50">
                  <option value="">-- Select Destination Team --</option>
                  {teams.filter(t => t.clubId === destClubId).sort((a,b) => a.name.localeCompare(b.name)).map(t => (
                     <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <p className="text-xs text-blue-700 mt-2">Players will be appended safely to this team.</p>
              </div>
           </div>

           <div className="flex justify-end gap-3">
             <button 
               onClick={handleMigrate} 
               disabled={loading || !sourceTeamId || !destTeamId}
               className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg shadow font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
             >
               <Users size={20} />
               {loading ? 'Running...' : 'Phase 1: Copy Players'}
             </button>
           </div>
         </div>
       </div>

       <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
         <div className="p-6 bg-indigo-600 border-b border-indigo-700 text-white">
           <h2 className="text-2xl font-bold flex items-center gap-2"><CalendarDays size={24} /> Phase 2: Manual Game Duplication</h2>
           <p className="opacity-90 mt-1">Safely clone legacy games to duplicate teams by selecting them directly from the club.</p>
         </div>

         <div className="p-6">
           <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">Currently bridging <strong>{clubs.find(c=>c.id===sourceClubId)?.name || '...'}</strong> → <strong>{clubs.find(c=>c.id===destClubId)?.name || '...'}</strong></p>
              <p className="text-sm text-gray-700">Destination Team Filter: <strong>{teams.find(t=>t.id===destTeamId)?.name || 'None selected (Required)'}</strong></p>
           </div>

           {/* Manual Game Selection */}
           {clubGames.length > 0 && sourceClubId ? (
             <div className="mb-8 border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="p-3 bg-gray-50 border-b font-bold flex justify-between items-center text-sm">
                  <span>Select Legacy Games to Clone ({selectedGameIds.size}/{clubGames.length})</span>
                  <button onClick={() => {
                    if(selectedGameIds.size === clubGames.length) setSelectedGameIds(new Set());
                    else setSelectedGameIds(new Set(clubGames.map(g=>g.id)));
                  }} className="text-indigo-600 hover:text-indigo-800">
                    {selectedGameIds.size === clubGames.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                  {clubGames.map(game => (
                    <label key={game.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors">
                      <input 
                        type="checkbox"
                        checked={selectedGameIds.has(game.id)}
                        onChange={(e) => {
                          const newSet = new Set(selectedGameIds);
                          if(e.target.checked) newSet.add(game.id);
                          else newSet.delete(game.id);
                          setSelectedGameIds(newSet);
                        }}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{game.name || `${game.homeTeam} vs ${game.awayTeam} `}</p>
                        <p className="text-xs text-gray-500">
                           {game.date} • {game.teamId ? teams.find(t=>t.id===game.teamId)?.name : 'Legacy untethered game'}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
             </div>
           ) : (
                <div className="mb-8 p-4 bg-gray-50 text-gray-500 text-center rounded-lg border border-gray-200 border-dashed">
                  Select a Source Club above to load all legacy games.
                </div>
           )}

           <div className="flex justify-end gap-3">
             <button 
               onClick={handleMigrateGames} 
               disabled={loading || selectedGameIds.size === 0 || !destClubId || !destTeamId}
               className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg shadow font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
             >
               <CalendarDays size={20} />
               {loading ? 'Duplicating...' : `Phase 2: Duplicate ${selectedGameIds.size} Games`}
             </button>
           </div>
         </div>
       </div>
    </div>
  );
}
